/*
 * The footer both shells end with - chosen per deployment.
 *
 * The knowledge base runs as more than one instance from one artefact, and each
 * may end its pages with its own company's footer. Which one is the App Service
 * setting SITE_FOOTER_SCOPE, served on /api/config (shared/siteFooter.ts has the
 * list and the parser):
 *
 *   accoTOOL   CompanyFooter with ACCOTOOL_FOOTER - the logo, the strapline,
 *              LinkedIn and the links back to accotool.com, matching that site's
 *              own footer.
 *   BIaaS      CompanyFooter with BIAAS_FOOTER - Accobat's logo, the BIaaS
 *              strapline, LinkedIn and the links back to accobat.com
 *              (2026-10-27; the default until then).
 *   missing or unrecognised
 *              the default - one line, the site's name, the footer this site had
 *              before footers were chosen. The owner's rule, revised 2026-10-08:
 *              no footer specified means the previous footer, not none.
 *
 * Nothing renders until /api/config has answered, so a deployment with its own
 * footer never shows the default first and then swaps. The footer sits at the
 * bottom of the page, and the response is small and database-free, so the wait
 * is rarely seen.
 *
 * It stays ONE component rendered by both layouts, because two spellings of a
 * footer drift. The light/dark switch it once carried lives in the header now
 * (ColorSchemeSwitch.tsx).
 */

import { useClientConfig } from '../auth/AuthProvider.js';
import { CompanyFooter } from './CompanyFooter.js';
import { ACCOTOOL_FOOTER, BIAAS_FOOTER } from './companyFooters.js';
import { FALLBACK_SITE_TITLE, useSiteBranding } from './SiteBranding.js';

export function SiteFooter(): JSX.Element | null {
  const config = useClientConfig();
  if (!config) return null;

  switch (config.footer.scope) {
    case 'accoTOOL':
      return <CompanyFooter content={ACCOTOOL_FOOTER} />;
    case 'BIaaS':
      return <CompanyFooter content={BIAAS_FOOTER} />;
    default:
      // Every deployment that names no footer, or one this build does not know.
      return <DefaultFooter />;
  }
}

/*
 * The site's name, from the branding provider as the header's is, so the two
 * cannot disagree; the fallback is the same generic title the brand shows before
 * anything has loaded.
 */
function DefaultFooter(): JSX.Element {
  const { site } = useSiteBranding();

  return (
    <footer className="site-footer">
      <div className="page site-footer-inner">
        <p className="site-footer-title">{site?.title ?? FALLBACK_SITE_TITLE}</p>
      </div>
    </footer>
  );
}
